/**
 * Campanita del centro de notificaciones (ADR 0022 — fase 4).
 *
 * Se monta en dos lugares (`App.tsx` para el dashboard del tenant,
 * `AdminShell.tsx` para la consola de plataforma) porque los productores de
 * hoy notifican a AMBOS planos — el único productor real mergeado
 * (`email-failure-inbox.ts`) le habla a `superAdmin`, que vive enteramente
 * en `/admin`. Un solo montaje en el dashboard tenant lo dejaría sin ver sus
 * propias notificaciones.
 *
 * Toda la lógica (fetch inicial, WS, backoff, catch-up, dedupe) vive en
 * `useNotificationCenter` — este archivo es presentación + el cableado de
 * UI que el hook deliberadamente no conoce (toasts, navegación al click).
 *
 * Sin des-ocultar por vista de archivo: el worker NO expone un endpoint para
 * LISTAR ocultas (`listVisibleNotifications` siempre filtra `hiddenAt IS
 * NULL` — ver `notification.ts` de persistencia), así que una pantalla
 * "Ocultas" no tiene de dónde traer datos. En su lugar, ocultar dispara un
 * toast con acción "Deshacer" que llama a `unhide` mientras el id sigue en
 * memoria de esta sesión — cubre el caso real (me equivoqué al ocultar)
 * sin inventar una vista que el backend no puede alimentar. Si en el futuro
 * se agrega un endpoint de listado de ocultas, una vista "Ocultas" persistente
 * es la extensión natural.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import {
  describeConnectionStatus,
  formatRelativeTime,
  resolveNotificationTarget,
} from "../../lib/notifications/notification-policy";
import type { NotificationDTO } from "../../lib/notifications/types";
import { useNotificationCenter } from "../../lib/notifications/useNotificationCenter";
import "./notifications.css";

const UNREAD_BADGE_CAP = 9;

/** Texto del badge numérico — capado en "9+" para no romper el layout de la campanita. */
export function formatUnreadBadge(count: number): string {
  return count > UNREAD_BADGE_CAP ? `${UNREAD_BADGE_CAP}+` : String(count);
}

/**
 * `aria-label` del botón trigger. El conteo no puede depender solo del
 * badge visual (color/tamaño) — un lector de pantalla necesita el número
 * en texto, no en un `<span>` decorativo.
 */
export function describeBellAriaLabel(unreadCount: number): string {
  if (unreadCount === 0) return "Notificaciones, sin novedades";
  if (unreadCount === 1) return "Notificaciones, 1 sin leer";
  return `Notificaciones, ${unreadCount} sin leer`;
}

type BellVariant = "header" | "sidebar" | "mobile";

interface NotificationBellProps {
  /** Controla el anclaje visual del panel — el comportamiento es idéntico en los tres. */
  variant?: BellVariant;
}

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

interface NotificationRowProps {
  notification: NotificationDTO;
  onOpen: (n: NotificationDTO) => void;
  onHide: (n: NotificationDTO) => void;
}

function NotificationRow({ notification, onOpen, onHide }: NotificationRowProps) {
  const unread = notification.readAt === null;
  return (
    <li className={`notif-item${unread ? " notif-item--unread" : ""}`}>
      <button type="button" className="notif-item-body" onClick={() => onOpen(notification)}>
        <span className="notif-item-dot" aria-hidden="true" />
        <span className="notif-item-text">
          <span className="notif-item-title">{notification.title}</span>
          {notification.body && <span className="notif-item-desc">{notification.body}</span>}
          <span className="notif-item-time">{formatRelativeTime(notification.createdAt)}</span>
        </span>
      </button>
      <button
        type="button"
        className="notif-item-hide"
        onClick={() => onHide(notification)}
        aria-label={`Ocultar notificación: ${notification.title}`}
        title="Ocultar"
      >
        ✕
      </button>
    </li>
  );
}

export function NotificationBell({ variant = "header" }: NotificationBellProps) {
  const {
    notifications,
    unreadCount,
    connectionPhase,
    lastSyncAt,
    loading,
    error,
    markRead,
    hide,
    unhide,
  } = useNotificationCenter();

  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Click afuera / Escape cierran el panel — mismo patrón que
  // UserFooterPopover / CompanySelector de AdminShell.tsx.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const handleOpenNotification = (n: NotificationDTO) => {
    if (n.readAt === null) void markRead(n.id);
    const target = resolveNotificationTarget(n);
    if (target) {
      setOpen(false);
      navigate(target);
    }
  };

  const handleHide = (n: NotificationDTO) => {
    void hide(n.id)
      .then(() => {
        toast("Notificación ocultada", {
          action: { label: "Deshacer", onClick: () => void unhide(n.id) },
        });
      })
      .catch(() => {
        toast.error("No se pudo ocultar la notificación");
      });
  };

  return (
    <div className={`notif-bell notif-bell--${variant}`} ref={wrapRef}>
      <button
        type="button"
        className="notif-bell-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={describeBellAriaLabel(unreadCount)}
        data-tooltip={variant === "sidebar" ? "Notificaciones" : undefined}
      >
        <BellIcon />
        {variant === "sidebar" && <span className="notif-bell-label">Notificaciones</span>}
        {unreadCount > 0 && (
          <span className="notif-bell-badge" aria-hidden="true">
            {formatUnreadBadge(unreadCount)}
          </span>
        )}
      </button>

      {open && (
        <section className="notif-panel" aria-label="Notificaciones">
          <div className="notif-panel-header">
            <span className="notif-panel-title">Notificaciones</span>
            <span
              className={`notif-panel-status${
                connectionPhase === "open" ? " notif-panel-status--live" : ""
              }`}
            >
              <span className="notif-panel-status-dot" aria-hidden="true" />
              {describeConnectionStatus(connectionPhase, lastSyncAt)}
            </span>
          </div>

          {error && (
            <div className="notif-panel-error" role="alert">
              {error}
            </div>
          )}

          {loading && notifications.length === 0 && (
            <div className="notif-panel-loading">
              <div className="spinner" />
            </div>
          )}

          {!loading && notifications.length === 0 && !error && (
            <div className="notif-panel-empty">Sin notificaciones</div>
          )}

          {notifications.length > 0 && (
            <ul className="notif-list">
              {notifications.map((n) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  onOpen={handleOpenNotification}
                  onHide={handleHide}
                />
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
